import React, { ReactNode, useContext, useEffect, useState } from 'react';

import UbiquitiHostDnsPoliciesContext from '@totem/components/ubiquiti/hosts/details/dnsPolicies/ubiquitiHostDnsPoliciesContext';
import {
  UbiquitiApiDataItem,
  UbiquitiDnsPolicy,
} from '@totem/components/ubiquiti/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { UBIQUITI_HOSTS_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  hostId: string;
  children?: ReactNode;
};

// Standalone by design (takes hostId as a prop, not from route params) so it
// can be embedded anywhere a host's DNS policy list is needed, not just the
// details drill-down page.
const UbiquitiHostDnsPoliciesContainer = ({ hostId, children }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [dnsPolicies, setDnsPolicies] = useState<
    UbiquitiApiDataItem<UbiquitiDnsPolicy>[]
  >([]);

  useEffect(() => {
    setLoading(true);

    setError(false);

    // Host IDs contain a literal colon (e.g. "D8B3...:1815601434"), which is
    // legal in a URL path segment per RFC 3986 but not guaranteed to survive
    // every intermediate proxy/gateway unencoded — encode defensively.
    fetch(
      `${UBIQUITI_HOSTS_ENDPOINT}/${encodeURIComponent(hostId)}/dnsPolicies`,
      {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      },
    )
      .then((res) => {
        // A non-2xx response body is an APIErrorResponse object, not a DNS
        // policy array — never hand that to the table as dataSource.
        if (!res.ok) {
          setError(true);
          return [];
        }
        return res.json();
      })
      .then((result: UbiquitiApiDataItem<UbiquitiDnsPolicy>[]) => {
        setDnsPolicies(Array.isArray(result) ? result : []);
      })
      .then(() => {
        setLoading(false);
      })
      .catch(() => {
        setError(true);

        setDnsPolicies([]);

        setLoading(false);
      });
  }, [hostId, getApiToken]);

  return (
    <UbiquitiHostDnsPoliciesContext.Provider
      value={{ loading, error, dnsPolicies }}
    >
      <div>{children}</div>
    </UbiquitiHostDnsPoliciesContext.Provider>
  );
};

export default UbiquitiHostDnsPoliciesContainer;
